import { useMemo, useRef, useState } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import { Button, message, Select, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import DocumentUploader from '../../../components/Upload/DocumentUploader';
import { POLICY_ALLOWED_EXTENSIONS } from '../../PolicyExtraction/utils/fileValidation';
import { POLICY_CONFIGURATION_BUILDING_BLOCKS } from '../constants/buildingBlocks';
import styles from './Upload.module.scss';

const { Paragraph } = Typography;

const UPLOAD_ALLOWED_EXTENSIONS = POLICY_ALLOWED_EXTENSIONS.filter((ext) => ext === 'json');
const UPLOAD_ACCEPT_STRING = '.json';
const UPLOAD_ALLOWED_FORMAT_LABEL = 'JSON';

const isUploadFileAllowed = (fileName?: string): boolean => {
  if (!fileName) {
    return false;
  }
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension ? 
          UPLOAD_ALLOWED_EXTENSIONS.includes(extension as typeof UPLOAD_ALLOWED_EXTENSIONS[number]) 
          : false;
};

export type UploadPayload = {
  buildingBlockKey: string;
  files: File[];
};

interface UploadProps {
  onSubmit?: (payload: UploadPayload) => Promise<void> | void;
}

const Upload = ({ onSubmit }: UploadProps) => {
  const [buildingBlockKey, setBuildingBlockKey] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const buildingBlockOptions = useMemo(
    () =>
      POLICY_CONFIGURATION_BUILDING_BLOCKS.map((block) => ({
        value: block.key,
        label: `${block.label}`,
      })),
    [],
  );

  const handleFilesChange = (files: File[], nextFileList: UploadFile[]) => {
    const validFiles = files.filter((file) => isUploadFileAllowed(file.name));
    const filteredFileList = nextFileList.filter((item) => isUploadFileAllowed(item.name));

    if (files.length > 0 && validFiles.length !== files.length) {
      message.error(`Unsupported file type. Allowed formats: ${UPLOAD_ALLOWED_FORMAT_LABEL}.`);
    }

    setSelectedFiles(validFiles);
    setFileList(filteredFileList);
  };

  const handleSubmit = async () => {
    if (inFlight.current) return;
    if (!buildingBlockKey) {
      message.warning('Select a building block.');
      return;
    }

    if (selectedFiles.length === 0) {
      message.warning('Select a document before uploading.');
      return;
    }

    try {
      inFlight.current = true;
      setSubmitting(true);
      await onSubmit?.({
        buildingBlockKey,
        files: selectedFiles,
      });
      setBuildingBlockKey('');
      setSelectedFiles([]);
      setFileList([]);
    } catch (error) {
      message.error('Failed to upload policy document.');
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.selectGroup}>
        <Select
          placeholder="Select building block"
          options={buildingBlockOptions}
          value={buildingBlockKey || undefined}
          onChange={(value) => setBuildingBlockKey(value ?? '')}
          disabled={submitting}
          className={styles.select}
          allowClear
        />
      </div>
      <Paragraph className="!text-[#9ca3af]">Supported formats: {UPLOAD_ALLOWED_FORMAT_LABEL}</Paragraph>
      <div className={styles.uploader}>
        <DocumentUploader
          className={`${styles.dragger} h-full`}
          fileList={fileList}
          accept={UPLOAD_ACCEPT_STRING}
          onFilesChange={handleFilesChange}
          multiple
          disabled={submitting}
        />
      </div>
      <div className={styles.actions}>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleSubmit}
          loading={submitting}
          disabled={submitting}
          className={styles.uploadButton}
        >
          Upload
        </Button>
      </div>
    </div>
  );
};

export default Upload;
